import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { pairwise } from 'rxjs/operators';
import { ParamPhoneMaskHn, ParamPhoneMaskNoHn } from 'src/app/@core/constants';
import { ObjectUtils } from 'src/app/@core/utils/object-utils';
import { City } from 'src/app/models/city';
import { Colony } from 'src/app/models/colony';
import { Nationality } from 'src/app/models/nationality';
import { OnbaseCatalogue } from 'src/app/models/onbase-catalogue';
import { OnbaseEconomicActivity } from 'src/app/models/onbase-economic-activity';
import { Region } from 'src/app/models/region';
import { CityService } from 'src/app/services/city.service';
import { ColonyService } from 'src/app/services/colony.service';
import { NationalityService } from 'src/app/services/nationality.service';
import { OnbaseService } from 'src/app/services/onbase.service';
import { ParamsService } from 'src/app/services/params.service';
import { RegionService } from 'src/app/services/region.service';

@Component({
  selector: 'app-independent-income',
  templateUrl: './independent-income.component.html',
  styleUrls: ['./independent-income.component.sass']
})
export class IndependentIncomeComponent implements OnInit {

  constructor(private nationalityService: NationalityService, private cityService: CityService, private regionService: RegionService, private colonyService: ColonyService, private paramService: ParamsService, private onbaseService: OnbaseService) { }

  @Input() independentForm: FormGroup = new FormGroup({
    businessName: new FormControl("", Validators.required),
    incomeAmount: new FormControl("", [Validators.min(1), Validators.required]),
    economicActivity: new FormControl("", Validators.required),
    businessRelationType: new FormControl("", Validators.required),
    businessRelationDate: new FormControl("", Validators.required),
    country: new FormControl("", Validators.required),
    deparment: new FormControl("", Validators.required),
    city: new FormControl("", Validators.required),
    neighborhood: new FormControl("", Validators.required),
    street: new FormControl(""),
    street2: new FormControl(""),
    street3: new FormControl(""),
    street4: new FormControl(""),
    street5: new FormControl(""),
    companyPhone: new FormControl("", Validators.required),
    companyExtension: new FormControl(""),
  });
  @Input() isEdit: boolean = false;
  @Input() onbaseCatalogue: OnbaseCatalogue = new OnbaseCatalogue();
  @Input() hideStreet: boolean = false;
  @Input() readonly: boolean = false;
  nationalities: Array<Nationality> = [];
  departments: Array<Region> = [];
  cities: Array<City> = [];
  neighborhoods: Array<Colony> = [];
  phoneMaskHN: string = "";
  phoneMaskNonHN: string = "";
  currentMask: string = "";
  economicActivities: Array<OnbaseEconomicActivity> = [];
  businessRelationTypes: Array<{ key: string, value: string }> = [];
  @Input() isShortFormActiveForPlan = false;
  async ngOnInit(): Promise<void> {
    if (this.onbaseCatalogue.economicActivity.length == 0) {
      this.onbaseCatalogue = (await this.onbaseService.readOnBaseCatalogue().toPromise()).response;
    }
    this.nationalities = (await this.nationalityService.readNationalities().toPromise()).response.content;
    this.phoneMaskHN = (await this.paramService.readParam(ParamPhoneMaskHn).toPromise()).response.value;
    this.phoneMaskNonHN = (await this.paramService.readParam(ParamPhoneMaskNoHn).toPromise()).response.value;
    this.economicActivities = this.onbaseCatalogue.economicActivity;
    this.businessRelationTypes = ObjectUtils.objectToArray(this.onbaseCatalogue.relationType);
    await this.changeCountry({ code: this.independentForm.get("country")?.value, name: "", country: "" }, false);

    if (this.isEdit) {
      await this.changeCountry({ code: this.independentForm.get("country")?.value, name: "", country: "" }, false);
      await this.changeDepartment({ country: this.independentForm.value.country, id: '', name: '', region: this.independentForm.value.deparment }, false)
      await this.changeCity({ cityCode: this.independentForm.value.city, code: '', countryCode: '', name: '', regionCode: '' }, false);
    }

    this.independentForm.valueChanges.pipe(pairwise()).subscribe(async ([prev, next]) => {
      // if (next.country && prev.country != next.country && this.isEdit) {
      //   await this.changeCountry({ code: next.country, name: "", country: "" });
      // }
      if (next.deparment && next.deparment != prev.deparment && this.isEdit) {
        await this.changeDepartment({ country: next.country, id: '', name: '', region: next.deparment }, false)
      }
      if (next.city && next.city != prev.city && this.isEdit) {
        await this.changeDepartment({ country: next.country, id: '', name: '', region: next.deparment }, false)
        await this.changeCity({ cityCode: next.city, code: '', countryCode: '', name: '', regionCode: '' }, false);
      }

    })
  }
  async changeCountry(item: Nationality, reset: boolean = true): Promise<void> {
    this.departments = (await this.regionService.readRegions(item.code).toPromise()).response.content;

    this.resetFields(item, reset);
  }

  private resetFields(item: Nationality, reset: boolean) {
    if (item.code == "HN") {
      this.currentMask = this.phoneMaskHN;
    }
    else {
      this.currentMask = this.phoneMaskNonHN;
    }
    if (reset) {
      this.independentForm.get("deparment")?.reset();
      this.independentForm.get("city")?.reset();
      this.independentForm.get("neighborhood")?.reset();
      this.independentForm.get("street")?.reset();
      this.independentForm.get("companyPhone")?.reset();
    }
    this.independentForm.get("deparment")?.markAsDirty();
    this.independentForm.get("country")?.markAsDirty();
    this.independentForm.get("city")?.markAsDirty();
    this.independentForm.get("neighborhood")?.markAsDirty();
    this.independentForm.get("street")?.markAsDirty();
    this.independentForm.get("companyPhone")?.markAsDirty();
    this.cities = [];
    this.neighborhoods = [];

  }

  async changeDepartment(item: Region, reset: boolean = true): Promise<void> {
    this.cities = (await this.cityService.readCities(item.country, item.region).toPromise()).response.content;
    if (reset) {
      this.independentForm.get("neighborhood")?.reset();
      this.independentForm.get("city")?.reset();
    }
  }

  async changeCity(item: City, reset: boolean = true): Promise<void> {
    this.neighborhoods = (await this.colonyService.readColonies(item.cityCode).toPromise()).response.content;
    if (reset) {
      this.independentForm.get("neighborhood")?.reset();
    }
  }
}
